import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import { useCallback, useMemo, useRef, useState } from "react";
import { SafeAreaView, ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { ActivityIndicator, Snackbar, Text } from "react-native-paper";

import { TeachingEmptyState } from "./animation/TeachingEmptyState";
import { TeachingGeneratingAnimation } from "./animation/TeachingGeneratingAnimation";
import {
    TeachingReviewCards,
    type TeachingItem as ReviewItem
} from "./animation/TeachingReviewCards";

import {
    completeSection,
    deleteTeachingItem as apiDeleteTeachingItem,
    fetchTeachingItems,
    generateTeaching,
    updateTeachingItem as apiUpdateTeachingItem,
    type TeachingItem
} from "@/lib/api";
import { useFormContext } from "@/lib/FormContext";
import { colors, fonts, shadows, spacing } from "@/lib/theme";
import { useWebSocketEvent } from "@/lib/websocket";

/**
 * `none` is "generation ran and this visit has no teaching to give", which is
 * an ordinary outcome and NOT the same as `idle` ("nobody has generated yet").
 * They look identical from the items list alone, and conflating them is a dead
 * end: the Complete bar only renders for a settled phase, so an empty section
 * could never be completed, `allSectionsComplete` never went true, and the
 * visit could not be submitted at all.
 */
type Phase = "loading" | "idle" | "none" | "generating" | "ready" | "error";

/** Where to land once a load settles. */
function settled(itemCount: number, generationAttempted: boolean): Phase {
    if (itemCount > 0) {
        return "ready";
    }
    return generationAttempted ? "none" : "idle";
}

export default function TeachingScreen() {
    const { id: visitId } = useLocalSearchParams<{ id: string }>();
    const router = useRouter();
    const { form, updateForm } = useFormContext();

    const teachingSection = useMemo(
        () => form.phases.flatMap((p) => p.sections).find((sec) => sec.behaviorType === "teaching"),
        [form.phases]
    );

    const sectionNameById = useMemo(() => {
        const map = new Map<string, string>();
        form.phases.forEach((phase) => {
            phase.sections.forEach((sec) => {
                map.set(String(sec.id), sec.name);
            });
        });
        return map;
    }, [form.phases]);

    /**
     * What the "Live activity" panel narrates while teaching generates.
     *
     * Derived from the visit's OWN form rather than hardcoded in the panel. The
     * panel's fallback list is the standard assessment's sections, so on a
     * special visit it used to name Neurological and Cardiopulmonary — neither
     * of which is on the form — and never Fall Event or Wound Assessment
     * Detail. Deriving it here is correct for both visit kinds by construction,
     * and stays correct when a section is added or retired.
     *
     * The teaching section itself is excluded: it is the screen the items land
     * on, not a section teaching is read from.
     */
    const liveActivityContent = useMemo(
        () => ({
            sections: form.phases
                .flatMap((p) => p.sections)
                .filter((sec) => sec.behaviorType !== "teaching")
                .map((sec) => sec.name),
            answerCount: form.answers.length
        }),
        [form.phases, form.answers]
    );

    const [phase, setPhase] = useState<Phase>("loading");
    const [items, setItems] = useState<TeachingItem[]>([]);
    const [startedAt, setStartedAt] = useState(0);
    const [errorReason, setErrorReason] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<string | null>(null);
    const [completing, setCompleting] = useState(false);

    // Guards concurrent reloads: a focus-refresh and a WS event can both fire
    // loadItems(); the last one to start wins. Mirrors PocReviewStep's epoch ref.
    const loadEpochRef = useRef(0);

    const loadItems = useCallback(
        async (opts?: { settlePhase?: boolean }): Promise<TeachingItem[]> => {
            if (!visitId) {
                return [];
            }
            const epoch = ++loadEpochRef.current;
            try {
                const response = await fetchTeachingItems(visitId);
                if (epoch !== loadEpochRef.current) {
                    return response.items;
                }
                setItems(response.items);
                if (opts?.settlePhase) {
                    setPhase(settled(response.items.length, response.generationAttempted));
                }
                return response.items;
            } catch (err) {
                console.error("Failed to load teaching items:", err);
                if (epoch === loadEpochRef.current && opts?.settlePhase) {
                    setPhase("idle");
                }
                return [];
            }
        },
        [visitId]
    );

    // On focus: land in `ready` if teaching already exists, else `idle`. Never
    // clobbers an in-flight `generating` phase (that resolves via WS/its own load).
    useFocusEffect(
        useCallback(() => {
            setPhase((current) => (current === "generating" ? current : "loading"));
            void loadItems({ settlePhase: true }).then((existing) => {
                setPhase((current) => {
                    if (current === "generating" || current === "none") {
                        return current;
                    }
                    return existing.length > 0 ? "ready" : current;
                });
            });
        }, [loadItems])
    );

    useWebSocketEvent("teaching:generated", (eventData) => {
        if (String(eventData.visitId) !== visitId) {
            return;
        }
        void loadItems().then((next) => {
            setErrorReason(null);
            // A generation just finished, so an empty result IS the answer.
            setPhase(next.length > 0 ? "ready" : "none");
        });
    });

    useWebSocketEvent("teaching:failed", (eventData) => {
        if (String(eventData.visitId) !== visitId) {
            return;
        }
        setErrorReason(eventData.reason ?? "Teaching generation failed.");
        setPhase("error");
    });

    const handleGenerate = useCallback(async () => {
        if (!visitId) {
            return;
        }
        setErrorReason(null);
        setStartedAt(Date.now());
        setPhase("generating");
        try {
            await generateTeaching(visitId);
            // Success arrives via the teaching:generated WS event. The focus
            // refresh is the fallback if the socket drops.
        } catch (err) {
            console.error("Failed to start teaching generation:", err);
            const message =
                err && typeof err === "object" && "message" in err
                    ? String((err as { message: unknown }).message)
                    : "Could not start teaching generation.";
            setErrorReason(message);
            setPhase("error");
        }
    }, [visitId]);

    const handleChangeItem = useCallback(
        async (id: string, body: string) => {
            if (!visitId) {
                return;
            }
            const itemId = Number(id);
            // Optimistic local update; persist on save.
            setItems((prev) =>
                prev.map((it) => (it.id === itemId ? { ...it, content: body } : it))
            );
            try {
                await apiUpdateTeachingItem(visitId, itemId, body);
                setSnackbar("Teaching updated");
            } catch (err) {
                console.error("Failed to update teaching:", err);
                setSnackbar("Failed to save. Please try again.");
                void loadItems();
            }
        },
        [visitId, loadItems]
    );

    const handleDeleteItem = useCallback(
        async (id: string) => {
            if (!visitId) {
                return;
            }
            const itemId = Number(id);
            setItems((prev) => prev.filter((it) => it.id !== itemId));
            try {
                await apiDeleteTeachingItem(visitId, itemId);
                setSnackbar("Teaching removed");
            } catch (err) {
                console.error("Failed to delete teaching:", err);
                setSnackbar("Failed to remove. Please try again.");
                void loadItems();
            }
        },
        [visitId, loadItems]
    );

    const handleComplete = useCallback(async () => {
        if (!visitId || !teachingSection) {
            return;
        }
        setCompleting(true);
        try {
            await completeSection(visitId, teachingSection.id);
            updateForm((prev) => ({
                ...prev,
                sectionStatuses: [
                    ...prev.sectionStatuses.filter(
                        (st) => String(st.sectionId) !== String(teachingSection.id)
                    ),
                    {
                        id: teachingSection.id,
                        visitId,
                        sectionId: teachingSection.id,
                        status: "completed",
                        completedAt: new Date().toISOString()
                    }
                ]
            }));
            setSnackbar("Teaching complete");
            setTimeout(() => router.back(), 600);
        } catch (err) {
            console.error("Failed to complete teaching section:", err);
            setSnackbar("Failed to complete. Please try again.");
        } finally {
            setCompleting(false);
        }
    }, [visitId, teachingSection, updateForm, router]);

    const reviewItems = useMemo<ReviewItem[]>(
        () =>
            items.map((it) => ({
                id: String(it.id),
                section: sectionNameById.get(String(it.sectionId)) ?? "Section",
                body: it.content ?? ""
            })),
        [items, sectionNameById]
    );

    const goBack = useCallback(() => {
        if (router.canGoBack()) {
            router.back();
        } else {
            router.replace(`/visit/${visitId}`);
        }
    }, [router, visitId]);

    return (
        <SafeAreaView style={styles.safe}>
            <View style={styles.header}>
                <TouchableOpacity
                    style={styles.backRow}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 20 }}
                    activeOpacity={0.6}
                    onPress={goBack}
                >
                    <MaterialCommunityIcons name="chevron-left" size={22} color={colors.teal500} />
                    <Text style={styles.backText}>Back</Text>
                </TouchableOpacity>
                <Text style={styles.headerTitle}>Teaching</Text>
                {phase === "ready" && reviewItems.length > 0 ? (
                    <Text style={styles.subtitle}>{reviewItems.length} paragraphs ready</Text>
                ) : null}
            </View>

            {phase === "loading" ? (
                <View style={styles.center}>
                    <ActivityIndicator size="large" color={colors.teal500} />
                </View>
            ) : phase === "idle" || phase === "none" ? (
                <TeachingEmptyState onGenerate={handleGenerate} settled={phase === "none"} />
            ) : phase === "error" ? (
                <View style={styles.center}>
                    <MaterialCommunityIcons
                        name="alert-circle-outline"
                        size={44}
                        color={colors.textTertiary}
                    />
                    <Text style={styles.errorTitle}>Teaching generation failed</Text>
                    <Text style={styles.errorSubtitle}>
                        {errorReason ?? "Something went wrong. Please try again."}
                    </Text>
                    <TouchableOpacity
                        style={styles.retryBtn}
                        activeOpacity={0.85}
                        onPress={handleGenerate}
                    >
                        <Text style={styles.retryBtnText}>Try again</Text>
                    </TouchableOpacity>
                </View>
            ) : phase === "generating" ? (
                <TeachingGeneratingAnimation startedAt={startedAt} content={liveActivityContent} />
            ) : reviewItems.length === 0 ? (
                <View style={styles.center}>
                    <Text style={styles.emptyTitle}>No teaching to review</Text>
                    <Text style={styles.emptySubtitle}>
                        This visit has no sections with teaching content.
                    </Text>
                </View>
            ) : (
                <ScrollView contentContainerStyle={styles.content}>
                    <TeachingReviewCards
                        items={reviewItems}
                        onChangeItem={handleChangeItem}
                        onDeleteItem={handleDeleteItem}
                    />
                </ScrollView>
            )}

            {phase === "ready" || phase === "none" ? (
                <View style={styles.bottomBar}>
                    <View style={styles.bottomDivider} />
                    <TouchableOpacity
                        style={[
                            styles.completeBtn,
                            (!teachingSection || completing) && styles.completeBtnDisabled
                        ]}
                        onPress={handleComplete}
                        disabled={!teachingSection || completing}
                        activeOpacity={0.85}
                    >
                        {completing ? (
                            <ActivityIndicator size="small" color={colors.surface} />
                        ) : (
                            <Text style={styles.completeBtnText}>
                                {phase === "none"
                                    ? "No teaching to record - Complete"
                                    : "Complete Teaching"}
                            </Text>
                        )}
                    </TouchableOpacity>
                </View>
            ) : null}

            <Snackbar
                visible={snackbar !== null}
                onDismiss={() => setSnackbar(null)}
                duration={2500}
                style={styles.snackbar}
            >
                {snackbar ?? ""}
            </Snackbar>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    safe: { flex: 1, backgroundColor: colors.background },
    center: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: spacing.xl,
        gap: spacing.sm
    },
    header: {
        paddingHorizontal: spacing.lg,
        paddingTop: spacing.sm,
        paddingBottom: spacing.md
    },
    backRow: { flexDirection: "row", alignItems: "center", marginBottom: spacing.xs },
    backText: { color: colors.teal500, fontFamily: fonts.medium, fontSize: 16 },
    headerTitle: { color: colors.textPrimary, fontFamily: fonts.bold, fontSize: 28 },
    subtitle: {
        color: colors.textSecondary,
        fontFamily: fonts.regular,
        fontSize: 14,
        marginTop: 2
    },
    content: { padding: spacing.lg, paddingBottom: spacing.xl },
    emptyTitle: { color: colors.textPrimary, fontFamily: fonts.semiBold, fontSize: 18 },
    emptySubtitle: {
        color: colors.textSecondary,
        fontFamily: fonts.regular,
        fontSize: 14,
        textAlign: "center"
    },
    errorTitle: { color: colors.textPrimary, fontFamily: fonts.semiBold, fontSize: 18 },
    errorSubtitle: {
        color: colors.textSecondary,
        fontFamily: fonts.regular,
        fontSize: 14,
        textAlign: "center"
    },
    retryBtn: {
        marginTop: spacing.md,
        paddingHorizontal: spacing.xl,
        paddingVertical: spacing.sm,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: colors.teal500
    },
    retryBtnText: { color: colors.teal600, fontFamily: fonts.semiBold, fontSize: 15 },
    bottomBar: { backgroundColor: colors.surface },
    bottomDivider: { height: 1, backgroundColor: colors.border },
    completeBtn: {
        margin: spacing.lg,
        paddingVertical: spacing.md,
        borderRadius: 14,
        backgroundColor: colors.teal500,
        alignItems: "center",
        ...shadows.sm
    },
    completeBtnDisabled: { opacity: 0.5 },
    completeBtnText: { color: colors.surface, fontFamily: fonts.semiBold, fontSize: 16 },
    snackbar: { backgroundColor: colors.textPrimary }
});
